import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  beginDriveReauth,
  buildResumeUrlForCurrentPage,
  canBeginReauth,
  clearDriveApiToken,
  clearReauthAttempt,
  DRIVE_API_TOKEN_STORAGE_KEY,
  getDriveApiToken,
  isPublicDrivePath,
  markReauthExchangeSuccess,
  sanitizeDriveReturnUrl,
  setDriveApiToken
} from '../lib/auth-token';
import {
  createDriveAuthClient,
  DriveAuthApiError
} from '../lib/drive-auth-api';

type SessionUser = {
  email: string | null;
  userId: string;
};

function pickUserFromExchange(user: unknown): SessionUser | null {
  if (!user || typeof user !== 'object') return null;
  const record = user as Record<string, unknown>;
  const userId =
    (typeof record.id === 'string' && record.id) ||
    (typeof record.userId === 'string' && record.userId) ||
    null;
  if (!userId) return null;
  const email = typeof record.email === 'string' ? record.email : null;
  return { email, userId };
}

function stripAuthParamsFromUrl(): void {
  if (typeof window === 'undefined') return;
  const clean = sanitizeDriveReturnUrl(window.location.href);
  const target = new URL(clean);
  const next = `${target.pathname}${target.search}${target.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) {
    window.history.replaceState(null, '', next);
  }
}

type AuthBootstrapProps = {
  children: React.ReactNode;
  apiBaseUrl?: string;
};

/**
 * SSO bootstrap for Drive (drive.cynayd.com):
 * 1. ?code= (prefer) or ?sso_token= → exchange → store cynayd_drive_api_token
 * 2. Strip credentials from URL before product load
 * 3. AUTH_SESSION_EXPIRED / missing token → One /apps/resume (once)
 * 4. Fresh token failing session → terminal error (no loop)
 */
export function AuthBootstrap({ children, apiBaseUrl }: AuthBootstrapProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const isPublicShare = isPublicDrivePath(pathname);

  const authApi = useMemo(
    () => createDriveAuthClient(apiBaseUrl),
    [apiBaseUrl]
  );

  const [ready, setReady] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [terminalAuthError, setTerminalAuthError] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function resolveSession(
      fromExchange?: SessionUser | null
    ): Promise<'ok' | 'expired' | 'error'> {
      if (fromExchange?.email && getDriveApiToken()) {
        if (!cancelled) setSessionUser(fromExchange);
        return 'ok';
      }
      try {
        const session = await authApi.authSession();
        if (!cancelled) {
          setSessionUser({
            email: session.email ?? fromExchange?.email ?? null,
            userId: session.userId || fromExchange?.userId || ''
          });
        }
        return 'ok';
      } catch (e) {
        if (e instanceof DriveAuthApiError && e.status === 401) {
          if (!cancelled) setSessionUser(null);
          return 'expired';
        }
        if (!cancelled) setSessionUser(fromExchange ?? null);
        return fromExchange ? 'ok' : 'error';
      }
    }

    async function bootstrap() {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const ssoToken = params.get('sso_token');
        let exchangedUser: SessionUser | null = null;
        let justExchanged = false;

        if (code || ssoToken) {
          try {
            const result = await authApi.exchangeSsoCredential({
              code: code || undefined,
              token: ssoToken || undefined
            });
            const apiToken =
              result.apiToken ||
              (result as { token?: string }).token ||
              '';
            if (!apiToken) {
              throw new Error('SSO exchange did not return an API token');
            }
            setDriveApiToken(apiToken);
            markReauthExchangeSuccess();
            exchangedUser = pickUserFromExchange(result.user);
            justExchanged = true;
          } catch (e) {
            clearDriveApiToken();
            stripAuthParamsFromUrl();
            if (!cancelled) {
              setError(
                e instanceof Error ? e.message : 'SSO exchange failed'
              );
            }
          }
          stripAuthParamsFromUrl();
        }

        if (isPublicShare) {
          if (!cancelled) setReady(true);
          return;
        }

        let authRequired = true;
        try {
          const status = await authApi.authStatus();
          authRequired = status.authRequired !== false;
        } catch {
          // If status endpoint is unavailable, require token when none present
          authRequired = true;
        }

        if (!authRequired) {
          if (getDriveApiToken()) {
            await resolveSession(exchangedUser);
          }
          if (!cancelled) setReady(true);
          return;
        }

        if (!getDriveApiToken()) {
          const returnUrl = sanitizeDriveReturnUrl(window.location.href);
          if (canBeginReauth(returnUrl)) {
            beginDriveReauth({ reason: 'MISSING_TOKEN', returnUrl });
            return;
          }
          if (!cancelled) {
            setBlocked(true);
            setTerminalAuthError(
              'Sign-in could not be completed. Open Drive from CYNAYD One again.'
            );
            setReady(true);
          }
          return;
        }

        const sessionResult = await resolveSession(exchangedUser);
        if (sessionResult === 'expired') {
          if (justExchanged) {
            clearDriveApiToken();
            if (!cancelled) {
              setTerminalAuthError(
                'Your session could not be established. Contact support if this continues.'
              );
              setBlocked(true);
              setReady(true);
            }
            return;
          }
          const returnUrl = sanitizeDriveReturnUrl(window.location.href);
          if (canBeginReauth(returnUrl)) {
            beginDriveReauth({ reason: 'BOOTSTRAP_401', returnUrl });
            return;
          }
          clearDriveApiToken();
          if (!cancelled) {
            setTerminalAuthError(
              'Authentication failed after sign-in. Please try again later.'
            );
            setBlocked(true);
            setReady(true);
          }
          return;
        }

        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Auth bootstrap failed');
          try {
            const status = await authApi.authStatus();
            if (status.authRequired !== false && !getDriveApiToken()) {
              const returnUrl = sanitizeDriveReturnUrl(window.location.href);
              if (canBeginReauth(returnUrl)) {
                beginDriveReauth({ reason: 'MISSING_TOKEN', returnUrl });
                return;
              }
              setBlocked(true);
            } else if (getDriveApiToken()) {
              const sessionResult = await resolveSession(null);
              if (sessionResult === 'expired') {
                setTerminalAuthError('Session is not valid.');
                setBlocked(true);
              }
            }
          } catch {
            /* ignore */
          }
          setReady(true);
        }
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [authApi, isPublicShare, pathname, location.search]);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== DRIVE_API_TOKEN_STORAGE_KEY) return;
      if (e.newValue) {
        setBlocked(false);
        setTerminalAuthError(null);
        clearReauthAttempt();
        void (async () => {
          try {
            const session = await authApi.authSession();
            setSessionUser({
              email: session.email ?? null,
              userId: session.userId || ''
            });
          } catch {
            setSessionUser(null);
          }
        })();
        return;
      }
      setSessionUser(null);
      void (async () => {
        try {
          const status = await authApi.authStatus();
          if (status.authRequired !== false) setBlocked(true);
        } catch {
          setBlocked(true);
        }
      })();
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [authApi]);

  if (!ready) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5
        }}
      >
        <CircularProgress size={22} />
        <Typography color="text.secondary" variant="body2">
          Connecting…
        </Typography>
      </Box>
    );
  }

  if (blocked || terminalAuthError) {
    const resumeHref = buildResumeUrlForCurrentPage();
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 3,
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" component="h1">
          CYNAYD Drive
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
          {terminalAuthError ||
            'Open Drive from CYNAYD One to sign in. Direct password login is not available here.'}
        </Typography>
        {error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : null}
        <Button
          variant="contained"
          href={resumeHref}
          onClick={(ev) => {
            ev.preventDefault();
            clearReauthAttempt();
            beginDriveReauth({ reason: 'MISSING_TOKEN' });
          }}
        >
          Continue with CYNAYD One
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}

export default AuthBootstrap;
